import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './DpoPage.module.scss'

const emptyForm = {
  title: '',
  direction: '',
  hours: 72,
  price: 0,
  status: 'active',
}

const DpoPage = () => {
  const { t } = useTranslation('common')
  const STATUS_LABELS = {
    active: t('dpo.status_active'),
    closed: t('dpo.status_closed'),
    draft: t('dpo.status_draft'),
  }
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [me, setMe] = useState(null)
  const [details, setDetails] = useState(null)
  const [students, setStudents] = useState([])
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isMethodist = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('methodist') || me.primary_role === 'methodist'
  }, [me])

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/dpo/courses/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadMe = async () => {
    try {
      const r = await $api.get('/admin-panel/me/')
      setMe(r.data)
    } catch {
      setMe(null)
    }
  }

  useEffect(() => {
    loadMe()
    load()
  }, [])

  const openDetails = async (row) => {
    setDetails(row)
    setStudents([])
    try {
      const res = await $api.get(`/dpo/courses/${row.id}/students/`)
      setStudents(res.data?.results || res.data || [])
    } catch {
      setStudents([])
    }
  }

  const create = async () => {
    if (!form.title) {
      setNotify({ isOpen: true, message: t('dpo.enter_course_title'), type: 'warning', sound: 'warning' })
      return
    }
    try {
      await $api.post('/dpo/courses/', form)
      setNotify({ isOpen: true, message: t('dpo.course_created'), type: 'success', sound: 'success' })
      setCreateOpen(false)
      setForm(emptyForm)
      load()
    } catch {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('dpo.title_page')}</h1>
        {isMethodist && (
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            {t('dpo.new_course')}
          </Button>
        )}
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('dpo.section_in_dev')}</div>
        ) : list.length === 0 ? (
          <div className="unv-empty">{t('dpo.no_courses')}</div>
        ) : (
          <table className="unv-table unv-table--clickable">
            <thead>
              <tr>
                <th>{t('dpo.col_title')}</th>
                <th>{t('dpo.col_direction')}</th>
                <th>{t('dpo.col_hours')}</th>
                <th>{t('dpo.col_price')}</th>
                <th>{t('dpo.col_status')}</th>
                <th>{t('dpo.col_students')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} onClick={() => openDetails(c)}>
                  <td>{c.title || '—'}</td>
                  <td>{c.direction || c.direction_name || '—'}</td>
                  <td>{c.hours || '—'}</td>
                  <td>{c.price != null ? `${c.price} ${t('dpo.som')}` : '—'}</td>
                  <td>
                    <span
                      className={`unv-badge ${
                        c.status === 'active' ? 'unv-badge--success' : 'unv-badge--muted'
                      }`}
                    >
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                  </td>
                  <td>{c.students_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!details} onClose={() => setDetails(null)} fullWidth maxWidth="md">
        <DialogTitle>{details?.title || t('dpo.course_default')}</DialogTitle>
        <DialogContent dividers>
          {details && (
            <>
              <div className={styles.metaGrid}>
                <div>
                  <strong>{t('dpo.direction_label')}:</strong> {details.direction || '—'}
                </div>
                <div>
                  <strong>{t('dpo.hours_label')}:</strong> {details.hours || '—'}
                </div>
                <div>
                  <strong>{t('dpo.price_label')}:</strong> {details.price != null ? `${details.price} ${t('dpo.som')}` : '—'}
                </div>
                <div>
                  <strong>{t('dpo.status_label')}:</strong> {STATUS_LABELS[details.status] || details.status}
                </div>
              </div>
              <h3>{t('dpo.students_title')}</h3>
              {students.length === 0 ? (
                <div className="unv-empty">{t('dpo.no_students')}</div>
              ) : (
                <table className="unv-table">
                  <thead>
                    <tr>
                      <th>{t('dpo.col_full_name')}</th>
                      <th>{t('dpo.col_contacts')}</th>
                      <th>{t('dpo.col_enrolled_at')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s.id}>
                        <td>{s.full_name || '—'}</td>
                        <td>{s.email || s.phone || '—'}</td>
                        <td>{s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString('ru-RU') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('dpo.new_course')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('dpo.field_title')}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('dpo.field_direction')}
            value={form.direction}
            onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}
            margin="normal"
          />
          <TextField
            type="number"
            fullWidth
            label={t('dpo.field_hours')}
            value={form.hours}
            onChange={(e) => setForm((f) => ({ ...f, hours: Number(e.target.value) }))}
            margin="normal"
          />
          <TextField
            type="number"
            fullWidth
            label={t('dpo.field_price_som')}
            value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label={t('dpo.field_status')}
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            margin="normal"
          >
            <MenuItem value="active">{t('dpo.status_active')}</MenuItem>
            <MenuItem value="draft">{t('dpo.status_draft')}</MenuItem>
            <MenuItem value="closed">{t('dpo.status_closed')}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={create}>
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default DpoPage
